/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useId, useState, memo, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { useRouter } from 'next/router';
import { inspect } from 'util';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import {
  Button,
  Heading,
  InputTextField,
  Link,
  Types,
  CheckBox,
  Text,
  Icon,
  RichText,
  Messages,
  Accordion,
  eventUtil,
} from '@marriott/mi-ui-library'; //import common component
import {
  useClientEnvVarsStore,
  getUserDetails,
  resetUserDetails,
  useUserDetailsGlobalStore,
} from '@marriott/mi-store-utils';
import { useSession } from '@marriott/mi-headless-utils';
import { QrvType } from '@marriott/shared/mi-helper-utils';
import {
  userLogin,
  handleRedirect,
  clearRememberUserDetails,
  usePageContext,
  PageContext,
  REMEMBER_ME_KEY,
  MFA_OPTIONS_KEY,
  useCheckBreakpoint,
} from '../../modules';
import {
  clearSessionAndCookie,
  emailOrMemberNumberError,
  LAST_ATTEMPT_WARNING_KEY,
  LOCKEDOUT_ERROR_KEY,
  logger,
  loginACDLkeyGenerator,
  signEmailMemberIdAsterixRegex,
  signEmailRegex,
  signMobileRegex,
  updatePublicKey,
  updateUserLoginDataLayer,
} from '../../modules/utils';
import { useAccountCommonStore } from '../../modules/store/accountCommonStore';
import { useAccountPersistentStore } from '../../modules/store/accountPersistentStore';
import { encryptPassword, maskEmail, randDecrpytAPI } from '../../modules/utils/helper';
import {
  addSubDirectoryPrefix,
  checkChinaLocale,
  checkUSLocale,
  checkKOLocale,
  getCurrentUrlParams,
  checkAELocale,
} from '../../modules/utils/helper'; // import helper from utills
import {
  EAA_FCP_WARNING_MESSAGE_KEY,
  CHECKED_IN_MODAL_ID,
  constants,
  ENCRYPTED_DATA,
  ALREADY_COMBINED_ERROR_KEY,
  AUDIT_WARNING_MESSAGE,
  PWRD_ERROR_MSG,
  REDEMPTION_DTT_MODAL,
  USERNAME_INVALID_ERROR,
  TRACKING_CONSTANTS,
  TRACKING_PAGE_NAME,
  SIGN_IN_PAGE_WARN_MESSAGE,
  DTT_QUERY_PARAM,
  EVENT_NAME,
  STATUS_IN_BOOLEAN,
  MODEL_NAME,
  INVALID_EMAIL_OR_MEMBERERROR,
  flyoutModalKeys,
  PHOENIX_SEND_OTP_CHALLENGE_URL,
  SIGN_IN_PAGE_ID,
  LOOKUP_MEMBER_NUMBER_PATH,
} from '../../modules/utils/constants';
import { useBannerMessagesStore } from '../../modules/store/bannerMessagesStore';
import { replaceText } from '../../modules/utils/promotionHelper';
import { useAccountModelStore } from '../../modules/store/accountModelStore';
import { setupLinkInterception, useFlyoutNavigation } from '../../modules/utils/flyoutHelper';
import { SignInComponentProps, SignInDataType } from './SignIn.types';
import { SignInEmailSuccessModalStyle, StyledSignInContainerDiv } from './SignIn.styles';

const { log } = logger({})('SearchResults/searchResults');

/** return memoized signin form component */
export const SignIn = memo<SignInComponentProps | any>((props: SignInComponentProps | any) => {
  const [forgotPasswordLink, setForgotPasswordLink] = useState<string>('');
  const {
    setIsContentLoading,
    setSignInEmailSuccess,
    activeKey,
    signInEmailSuccess,
    pageErrorReset,
    setPageErrorReset,
    lookUpEmailSuccess,
    setLookUpEmailSuccess,
    lookUpMemberEmail,
  } = useAccountModelStore(state => state);

  const clientEnvVars = useClientEnvVarsStore.getState().envVarsObject;
  const {
    ACCEPT_LANGUAGE: currentLocale,
    HOMEPAGE_REDIRECT_URL: homePageRedirectUrl,
    IS_PASSWORD_ENCRYPT: isEncryptionEnable,
    SKIP_DECODE_URL_FOR_PATH: skipDecodeForPaths,
    TRACKING_PAGE_NAME: pageName,
  } = clientEnvVars;
  const {
    EYE_TOGGLE,
    SIGNIN,
    ACTIVATE_ONLINE_ACCOUNT,
    FORGOT_PASSWORD,
    REMEMBER_ME,
    CLEAR_REMEMBER_ME,
    INTERNAL,
    SWEEPSTAKES_SIGNIN,
  } = TRACKING_CONSTANTS;
  const router = useRouter();
  const overlay = router && router.query['overlay'];
  const isOverlay = overlay === 'true';
  const isIframe = useRef<boolean>(isOverlay);
  const CustomClickTrackValue = `Login Form`;
  const {
    updatePageErrors,
    pageErrors,
    resLookupFromHeader,
    mockTestFlag,
    isReservation,
    modalId,
    accountEvent,
    isInsideAccountContainer,
    isAccountModal,
    isDTT,
    isFlyOut,
    inlineLabel = true, // use this to change input to older version
    enablePhaseOne,
    uxlFailureBannerText,
    enablePhaseThree,
  } = props;
  const { isACDLenabled, datalayer, updateDatalayer } = useSession();
  const clientId = router && router.query['clientId'];
  const validClientID = typeof clientId === 'string' ? clientId : Array.isArray(clientId) ? clientId[0] || '' : '';
  const trackingPageName =
    !isOverlay && !isAccountModal && validClientID
      ? SWEEPSTAKES_SIGNIN
      : pageName || props?.trackingPageTitle || props?.trackingPageName || '';
  const pageContext = useContext(PageContext);
  const [isError, setIsError] = useState(false);
  const isDesktopViewPort = useCheckBreakpoint('viewportL');
  const checkBoxId = useId(); // create uuid for
  const [rememberMe, setRememberMe] = useState(true); // remeber me checkbox
  const [showPassword, setShowPassword] = useState(false); // password toogle
  const { sessionData, sessionTimedOutPage, isMobileAuthFeatureEnabled, isEAASignIn } = usePageContext();
  const [errorsList, setErrorsList] = useState<string[]>([]);

  useEffect(() => {
    if (pageErrorReset) {
      setErrorsList([]);
      setFormErrors({});
      setIsError(false);
      setPageErrorReset(false);
      updatePageErrors?.({});
    }
  }, [pageErrorReset, setPageErrorReset, updatePageErrors]);

  const { randData, setRandData, formErrors, setFormErrors } = useAccountCommonStore(state => state);

  /** read data from global store */
  const { name, rememberedUser, memberNumber } = useUserDetailsGlobalStore(state => state);

  const [wasRemeberMeFlag, setWasRemeberMe] = useState<boolean | undefined>(rememberedUser || false);
  const [formData, setFormData] = useState<SignInDataType>({});

  const [toggleAccoridon, setToggleAccordion] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(true);
  const [publicKey, setPublicKey] = useState('');
  const [showAccordionExampleErrorMsg, setShowAccordionExampleErrorMsg] = useState<boolean>(false);
  const { interPageErrorMsg, setInterPageErrorMsg, setBannerMsgs, setBannerId } = useBannerMessagesStore(
    state => state
  );
  const {
    loginEventDetails,
    setLoginEventDetails,
    setMemberDetailsUxl,
    isEAAEnrollFailed,
    setIsEAAEnrollFailed,
    setShowBrowserRegistrationCta,
    passwordChanged,
    setPasswordChanged,
  } = useAccountPersistentStore(state => state);
  const inputId = useId();
  const isChinaLocale = checkChinaLocale(currentLocale);
  const isUSLocale = checkUSLocale(currentLocale);
  const isKOLocale = checkKOLocale(currentLocale);
  const isAELocale = checkAELocale(currentLocale);
  const [toggleErrormsgAccoridon, setToggleErrormsgAccordion] = useState<boolean>(false);
  const [isFcpError, setIsFcpError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showEAASignInMsg, setShowEAASignInMsg] = useState(false);
  const { errorMessageType, inValidCredentials, rand } = router.query;
  /** toggle the password view to text view */
  const toggleShowPassword = () => setShowPassword(!showPassword);
  const isCheckedIn = modalId === CHECKED_IN_MODAL_ID;
  const HOMEPAGE_REDIRECT_URL = homePageRedirectUrl;
  const enableEncryption = isEncryptionEnable;
  const isRedemptionDTT = modalId === REDEMPTION_DTT_MODAL;

  const renderEmailSuccessSignInModal = enablePhaseOne && activeKey === flyoutModalKeys.SIGN_IN;
  const { handleFlyoutNavigation } = useFlyoutNavigation(updatePageErrors, uxlFailureBannerText);

  const [isResLookUpLoaded, setIsResLookUpLoaded] = useState<boolean>(false);
  useEffect(() => {
    const checkElementLoaded = () => {
      const element = document.querySelector(`[data-testid=${constants.RES_LOOKUP_ID}]`);
      if (element) {
        setIsResLookUpLoaded(true);
        clearInterval(intervalId);
      } else {
        setIsResLookUpLoaded(false);
      }
    };

    const intervalId = setInterval(checkElementLoaded, 1000);

    // Clean up the interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const handleClick = async (link: string) => {
    setIsContentLoading(true);
    await handleFlyoutNavigation(link);
    setIsContentLoading(false);
  };

  useEffect(() => {
    if (!enablePhaseThree) return;
    // Intercept clicks on the lookUpMemberNumber link
    const { interceptAnchorClicks } = setupLinkInterception(LOOKUP_MEMBER_NUMBER_PATH, () =>
      handleClick(LOOKUP_MEMBER_NUMBER_PATH)
    );

    const cleanup = interceptAnchorClicks(signInPageId);

    return () => {
      cleanup();
    };
  }, []);

  const isSignInFormValid = (): boolean => {
    let newErrors = {};
    setShowAccordionExampleErrorMsg(false);
    /**validate signing form field */
    if (
      (formData.email === '' || formData.email === undefined) &&
      (formData.pwrd === '' || formData.pwrd === undefined)
    ) {
      newErrors = {
        email: checkForCountry() ? props?.credentialsErrorMessage : props?.emailErrorLabel,
        pwrd: props?.pwrdErrorLabel,
      };
      // if user doesn't have email val
      setFormErrors(newErrors);
      return false;
    }
    if (formData.email === '' || formData.email === undefined) {
      newErrors = {
        email: checkForCountry() ? props?.credentialsErrorMessage : props?.emailErrorLabel,
      };
      // if user doesn't have email val
      setFormErrors(newErrors);
      return false;
    } else if (!wasRemeberMeFlag) {
      const isValidMobile = checkForCountry() && signMobileRegex.test(formData?.email);

      const hasEmailMemberIdAsterixError = formData?.email && signEmailMemberIdAsterixRegex.test(formData.email);

      const isEmailOrMemberNumberError = checkForCountry()
        ? !signEmailRegex?.test(formData?.email)
        : emailOrMemberNumberError(formData?.email);

      if (!isValidMobile && isEmailOrMemberNumberError) {
        let message = checkForCountry() ? props?.credentialsWarningMessage : props?.warningMessage;
        if (hasEmailMemberIdAsterixError) {
          message = props?.signInPageWarnMessage;
          setFormErrors({
            email: props?.signInInlineWarnMessage,
            pwrd: '',
          });
        } else if (isEmailOrMemberNumberError) {
          setFormErrors({
            email: props?.invalidEmailOrMemberError,
          });
        } else {
          setFormErrors({});
        }
        if (isCheckedIn || (isAccountModal && isReservation) || renderEmailSuccessSignInModal) {
          setErrorsList([message]);
          return false;
        } else if (updatePageErrors) {
          updatePageErrors({
            text: message,
            class: 'error-sev1',
            type: 'alert',
          });
          return false;
        }
        return false;
      }
    }
    if (formData.pwrd === '' || formData.pwrd === undefined) {
      newErrors = {
        pwrd: props?.pwrdErrorLabel,
      };
      // if user doesn't have passsword val
      setFormErrors(newErrors);
      return false;
    }

    setFormErrors({
      pwrd: undefined,
      email: undefined,
    });
    setIsFcpError(false);
    return true;
  };

  const checkForCountry = () => {
    return isMobileAuthFeatureEnabled || isChinaLocale || mockTestFlag;
  };

  const displaySigninError = (key: string[] = [], omitEmailError: boolean = false) => {
    /**display error post submit */
    setErrorsList([]);

    if (updatePageErrors && !isEAASignIn && !isAccountModal) {
      let emailFormError =
        key?.[0] === SIGN_IN_PAGE_WARN_MESSAGE ? props?.signInInlineWarnMessage : props?.inlineErrorMessage;
      if (omitEmailError) {
        emailFormError = undefined;
      }
      setFormErrors({
        email: emailFormError,
        pwrd: '',
      });
      const apiFailureErrorMessage = enablePhaseOne ? uxlFailureBannerText : pageContext?.uxlErrorMessage;
      const warningText = props[key?.[0]] ?? apiFailureErrorMessage;

      updatePageErrors({
        // page level error - to add logic at component level for account modal
        text: warningText,
        class: 'error-sev1',
        type: 'alert',
      });

      setIsError(true);
    } else if (isEAASignIn) {
      if (key?.includes(EAA_FCP_WARNING_MESSAGE_KEY)) {
        setIsFcpError(true);
        setFormErrors({
          email: '',
          pwrd: '',
        });
        setErrorsList([props?.eaaFcpWarningMessage]);
      } else {
        setFormErrors({
          email: checkForCountry() ? props?.credentialsErrorMessage : props?.emailErrorLabel,
          pwrd: props?.pwrdErrorLabel,
        });
        setErrorsList([props?.credentialsWarningMessage]);
      }
      setIsError(true);
    } else if (isAccountModal) {
      setErrorsList([props?.[key?.[0]]]);
      setFormErrors({
        email: omitEmailError ? undefined : '',
        pwrd: '',
      });
      setIsError(true);
    } else if (renderEmailSuccessSignInModal) {
      setErrorsList([props?.[key?.[0]]]);
      setFormErrors({
        email: key?.[0] === SIGN_IN_PAGE_WARN_MESSAGE ? props?.signInInlineWarnMessage : props?.inlineErrorMessage,
        pwrd: '',
      });
      setIsError(true);
    }
  };

  const resetFormFields = () => {
    setFormData({ ...formData, pwrd: '', email: wasRemeberMeFlag ? formData.email : '' });
  };

  // decrypt rand
  const randAPIDecryption = async (isDTT: boolean, randParam?: string) => {
    const paramValue = isDTT ? randParam?.replace(DTT_QUERY_PARAM, '') : randParam;

    if (paramValue) {
      try {
        const response = await randDecrpytAPI(paramValue, isDTT);
        if (response?.status === 200) {
          setRandData({
            firstName: response?.data?.firstName ?? '',
            lastName: response?.data?.lastName ?? '',
            tripDetails: response?.data?.tripDetails,
          });
        }
      } catch (error) {
        updatePageErrors({
          text: enablePhaseOne ? uxlFailureBannerText : pageContext?.uxlErrorMessage,
          class: 'error-sev1',
          type: 'alert',
        });
        setIsError(true);
      }
    }
  };

  // TODO: signin return to url needs to be handled, it will be used if available form event
  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    const isDttInParam = rand && rand?.includes(DTT_QUERY_PARAM);
    ev.preventDefault(); // prevent loading of the page on submit
    setIsLoading(true);
    const urlParams = getCurrentUrlParams(isOverlay);
    setSignInEmailSuccess(false);
    setLookUpEmailSuccess(false);
    if (isSignInFormValid()) {
      /** if form is valid */
      try {
        const sessionToken =
          sessionData?.sessionToken || sessionData?.data?.sessionToken || sessionData?.data?.cacheData?.sessionToken;
        log.debug(`[User Login] submit Funtion called sessionID: ${sessionToken}`);
        const payload = wasRemeberMeFlag
          ? {
              pwrd: formData?.pwrd && encryptPassword(formData?.pwrd, publicKey, enableEncryption),
              email: '',
            }
          : {
              ...formData,
              pwrd: formData.pwrd && encryptPassword(formData?.pwrd, publicKey, enableEncryption),
            };
        const { data, status, headers } = await userLogin(
          sessionToken,
          trackingPageName,
          payload,
          rememberMe,
          isEAASignIn,
          isOverlay,
          currentLocale,
          accountEvent?.eventReturnUrl,
          skipDecodeForPaths,
          randData,
          rand && rand?.length > 0 ? isDttInParam : isDTT
        );

        const isMFA = data?.mfaOption && (data?.mfaOption?.emailAddress || data?.mfaOption?.phoneNumbers?.length > 0);
        if (data?.customerDetails) {
          clearSessionAndCookie();
          setMemberDetailsUxl?.(data?.customerDetails);
        }

        if (isMFA) {
          sessionStorage.setItem(MFA_OPTIONS_KEY, JSON.stringify(data?.mfaOption));
          sessionStorage.setItem(TRACKING_PAGE_NAME, trackingPageName);
          setShowBrowserRegistrationCta?.(data?.showBrowserRegistrationCta);
        }
        if (randData?.tripDetails?.tripId) {
          if (data?.isRedirect && !data?.mfaOption) {
            setBannerMsgs(constants?.RESERVATION_LINKED);
          } else {
            setBannerId(CHECKED_IN_MODAL_ID);
            sessionStorage.setItem(ENCRYPTED_DATA, data?.encodePayload);
          }
        }
        setIsError(false);
        // get the data from the user login api
        log.debug(
          `[User Login] success sessionID: ${
            sessionData?.sessionToken || sessionData?.data?.sessionToken || sessionData?.data?.cacheData?.sessionToken
          }, ${inspect(data)}`
        );
        if (!isMFA && status === 200) {
          const updateDatalayerPayload = {
            loginEventDetails: '',
            isSuccess: true,
            additionalDataLayerProperties: {},
            datalayerAction: undefined,
          };
          const loginEventDetailsFromHeader = headers?.['logineventdetails'] || '';
          // if ACDL is enabled, we are adding LOGIN event. if not, existing flow will be followed
          if (isACDLenabled) {
            Object.assign(updateDatalayerPayload, {
              additionalDataLayerProperties: loginACDLkeyGenerator(
                loginEventDetailsFromHeader,
                STATUS_IN_BOOLEAN.TRUE,
                datalayer
              ),
              datalayerAction: { eventName: EVENT_NAME.LOGIN, update: updateDatalayer },
            });
          } else {
            Object.assign(updateDatalayerPayload, {
              loginEventDetails: loginEventDetailsFromHeader,
            });
          }
          if (isACDLenabled || accountEvent?.successEvent) {
            updateUserLoginDataLayer(
              updateDatalayerPayload.loginEventDetails,
              updateDatalayerPayload.isSuccess,
              updateDatalayerPayload.additionalDataLayerProperties,
              updateDatalayerPayload.datalayerAction
            );
          }
          if (accountEvent?.successEvent) {
            eventUtil.dispatch(accountEvent?.successEvent, { data });
            return;
          }
        }
        if (data?.isRedirect) {
          if (
            enablePhaseThree &&
            isMFA &&
            !isEAASignIn &&
            data?.nextStateURI?.includes(PHOENIX_SEND_OTP_CHALLENGE_URL)
          ) {
            handleClick(data?.nextStateURI);
          } else {
            /** redrict user to next response url */
            handleRedirect(addSubDirectoryPrefix(data?.nextStateURI), isOverlay, !data?.mfaOption);
          }
        }
      } catch (error: any) {
        log.debug(
          `[User Login] error sessionID: ${
            sessionData?.sessionToken || sessionData?.data?.sessionToken || sessionData?.data?.cacheData?.sessionToken
          }, ${inspect(error)}`
        );

        // Set wrong password flag
        const isPasswordWrong = error?.response?.data?.message === PWRD_ERROR_MSG;

        // Set wrong username flag
        if (
          isChinaLocale &&
          (error?.response?.data?.message === USERNAME_INVALID_ERROR ||
            error?.response?.data?.message === INVALID_EMAIL_OR_MEMBERERROR)
        ) {
          setShowAccordionExampleErrorMsg(true);
        }

        // hardcoded check because, for sign in error we can get other error message as well when it is not mfa or fcp flow, and sign in happens but reservation is not linked
        if (randData?.tripDetails?.tripId) {
          setBannerMsgs(error?.response?.data?.phoenixErrorMessages?.errorMessages?.[0]);
        }
        if (error?.response?.data?.nextStateURI && error?.response?.data.isRedirect) {
          // redirect to appropriate url from backend
          handleRedirect(addSubDirectoryPrefix(error?.response?.data?.nextStateURI), isOverlay, !isOverlay);
        }

        if (isIframe?.current) {
          const param = new URLSearchParams(urlParams);
          let returnUrl = param.get('returnTo')
            ? `?returnTo=${encodeURIComponent(param.get('returnTo')!)}`
            : `?returnTo=${
                window.parent.location.pathname === '/sign-in.mi' ||
                window.parent.location.pathname === '/logout.mi' ||
                window.parent.location.pathname === '/sessionTimedOut.mi'
                  ? encodeURIComponent(addSubDirectoryPrefix(HOMEPAGE_REDIRECT_URL))
                  : encodeURIComponent(
                      addSubDirectoryPrefix(
                        param ? `${window.parent.location.pathname}?${param}` : window.parent.location.pathname
                      )
                    )
              }`;
          const clientId = param.get('clientId') ?? '';
          if (error?.response?.data?.phoenixErrorMessages?.errorMessages?.length) {
            const errMessageType = error?.response?.data?.phoenixErrorMessages?.errorMessages[0];
            returnUrl = `${returnUrl}&errorMessageType=${errMessageType}`;
          }

          if (wasRemeberMeFlag && isPasswordWrong) {
            returnUrl = `${returnUrl}&inValidCredentials=true`;
          }
          setLoginEventDetails?.(error?.response?.headers?.['logineventdetails'] || '');
          handleRedirect(
            addSubDirectoryPrefix(`/sign-in-error.mi${returnUrl}${clientId && `&clientId=${clientId}`}`),
            isOverlay,
            !isOverlay
          );
        } else {
          const updateDatalayerPayload = {
            loginEventDetails: '',
            isSuccess: false,
            additionalDataLayerProperties: {},
            datalayerAction: undefined,
          };
          // if ACDL is enabled, we are adding LOGIN event. if not, existing flow will be followed
          const loginEventDetailsFromHeader = error?.response?.headers?.['logineventdetails'] || '';
          if (isACDLenabled) {
            Object.assign(updateDatalayerPayload, {
              additionalDataLayerProperties: loginACDLkeyGenerator(
                loginEventDetailsFromHeader,
                STATUS_IN_BOOLEAN.FALSE,
                datalayer
              ),
              datalayerAction: { eventName: EVENT_NAME.LOGIN, update: updateDatalayer },
            });
          } else {
            Object.assign(updateDatalayerPayload, {
              loginEventDetails: loginEventDetailsFromHeader,
            });
          }
          updateUserLoginDataLayer(
            updateDatalayerPayload.loginEventDetails,
            updateDatalayerPayload.isSuccess,
            updateDatalayerPayload.additionalDataLayerProperties,
            updateDatalayerPayload.datalayerAction
          );
          // display error on component
          // for OCJ flow, we redirect user to findReservationList page with error message when not trip is not linked
          // here we do not need to show error message on sign in page when getting redirected
          !(randData?.tripDetails?.tripId && error?.response?.data?.nextStateURI && error?.response?.data.isRedirect) &&
            displaySigninError(
              error?.response?.data?.phoenixErrorMessages?.errorMessages,
              wasRemeberMeFlag && isPasswordWrong
            );
          resetFormFields();
          setIsLoading(false);
        }
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleClearRememberUserClick = async () => {
    const sessionToken =
      sessionData?.sessionToken || sessionData?.data?.sessionToken || sessionData?.data?.cacheData?.sessionToke;
    try {
      log.debug(`[User Remember] function called sessionID: ${sessionToken}`);
      const response = await clearRememberUserDetails(sessionToken, isEAASignIn, isOverlay);
      if (response) {
        /**if 200 then remove remember flag */
        resetUserDetails();
        isOverlay ? window.parent.location.reload() : window.location.reload();
      }
    } catch (error) {
      log.debug(`[User Remember] error sessionID: ${sessionToken}, ${inspect(error)}`);
    }
  };

  const shouldShowErrorMessage = useMemo(() => {
    return isEAASignIn || (!(isMobileAuthFeatureEnabled || mockTestFlag) && !showAccordionExampleErrorMsg);
  }, [isEAASignIn, isMobileAuthFeatureEnabled, mockTestFlag, showAccordionExampleErrorMsg]);

  const getUserRemeberDetails = useCallback(() => {
    try {
      const sessionToken =
        sessionData?.sessionToken || sessionData?.data?.sessionToken || sessionData?.data?.cacheData?.sessionToken;
      log.debug(`[User Remember] function called sessionID: ${sessionToken}`);
      /**
       * TODO:  need to find a way to avoid force fetch on this
       */
      getUserDetails(true, isDTT, {
        isEAA: isEAASignIn ?? '',
      });
    } catch (e) {
      log.debug(`[User Remember] error sessionID: ${sessionData?.sessionToken}, ${inspect(e)}`);
    } finally {
      setDisableButton(false);
    }
  }, []);

  useEffect(() => {
    isAccountModal && accountEvent?.modalType === modalId && getUserRemeberDetails();
  }, [accountEvent]);

  const handleKeyPress = (ev: any) => {
    /**
     * handle key press event on input field that will submit the
     * form
     */
    if (ev.key === 'Enter' || ev.keyCode === 13) {
      handleSubmit(ev);
    }
  };

  useEffect(() => {
    sessionStorage.setItem(REMEMBER_ME_KEY, JSON.stringify(rememberMe));
    !isAccountModal && getUserRemeberDetails();
    if (isEAASignIn && props?.forgotPwrdCtaPath) {
      const updatedLink = props.forgotPwrdCtaPath + getCurrentUrlParams();
      setForgotPasswordLink(updatedLink);
    } else {
      const parentLocation = window.parent.location;
      const isReturnTo = parentLocation.search;
      const param = new URLSearchParams(isReturnTo);
      const returnUrl =
        param.get('returnTo') || `${parentLocation.pathname + parentLocation.search + parentLocation.hash}`;
      const queryParam = `${props?.forgotPwrdCtaPath?.includes('?') ? '&' : '?'}returnTo=${encodeURIComponent(
        returnUrl
      )}`;
      setForgotPasswordLink(props?.forgotPwrdCtaPath + queryParam);
    }

    if (window.location.pathname?.includes('/sign-in-error') || mockTestFlag) {
      if (errorMessageType) {
        displaySigninError([`${errorMessageType}`], inValidCredentials === 'true'); // to show errors on the sign-in-error page
      } else if (sessionData?.cacheData?.data?.AriesAuth?.errorMessages?.apiErrors) {
        const apiError = sessionData?.cacheData?.data?.AriesAuth?.errorMessages?.apiErrors;
        if (apiError[LAST_ATTEMPT_WARNING_KEY]) {
          displaySigninError(['lockoutWarningMessage']);
        } else if (apiError[LOCKEDOUT_ERROR_KEY]) {
          displaySigninError(['lockedWarningMessage']);
        } else if (apiError[ALREADY_COMBINED_ERROR_KEY]) {
          displaySigninError(['alreadyCombinedError']);
        } else if (apiError[AUDIT_WARNING_MESSAGE]) {
          displaySigninError(['auditWarningMessage']);
        } else if (apiError[PWRD_ERROR_MSG] || apiError[USERNAME_INVALID_ERROR]) {
          displaySigninError([checkForCountry() ? 'credentialsWarningMessage' : 'warningMessage']);
        } else {
          displaySigninError();
        }
      } else {
        displaySigninError(); // to show errors on the sign-in-error page
      }
    }
    updatePublicKey(setPublicKey);
    rand && typeof rand === 'string' && randAPIDecryption(rand?.includes(DTT_QUERY_PARAM), rand);
  }, []);

  useEffect(() => {
    if (interPageErrorMsg) {
      updatePageErrors?.({
        text: props?.[interPageErrorMsg],
        class: 'error-sev1',
        type: 'alert',
      });
      setInterPageErrorMsg('');
    }
  }, [interPageErrorMsg]);

  useEffect(() => {
    if (isEAAEnrollFailed) {
      setShowEAASignInMsg(true);
      setIsEAAEnrollFailed?.(false);
    }
  }, [isEAAEnrollFailed]);

  useEffect(() => {
    if (passwordChanged) {
      props?.setIsPasswordChanged(true);
      props?.setSuccessMsg(props.passwordChangedSuccessMessage);
      setPasswordChanged?.(false);
    }
  }, [passwordChanged]);

  useEffect(() => {
    if (window.location.pathname?.includes('/sign-in-error') || mockTestFlag) {
      const globalHeaderTrackingPageName = sessionStorage.getItem(TRACKING_PAGE_NAME) || '';
      if (loginEventDetails || globalHeaderTrackingPageName) {
        if (!isACDLenabled) {
          updateUserLoginDataLayer(loginEventDetails || globalHeaderTrackingPageName, false);
        }
        setLoginEventDetails?.('');
        globalHeaderTrackingPageName && sessionStorage.removeItem(TRACKING_PAGE_NAME);
      }
    }
  }, [loginEventDetails, isACDLenabled]);

  useEffect(() => {
    /*When error messages are added to the container, the iframe height will be adjusted accordingly. */
    if (isOverlay && isError) {
      setIframeHeight();
    }
  }, [isError, rememberMe, formErrors, pageErrors]);

  useEffect(() => {
    if (isOverlay) {
      setIframeHeight();
    }
  }, [rememberMe]);

  useEffect(() => {
    setFormData({
      ...formData,
      email: memberNumber,
    });
    setWasRemeberMe(rememberedUser);
  }, [memberNumber, rememberedUser]);

  /*setIframeHeight function will updates the height of Iframe */
  const setIframeHeight = () => {
    const iframe: any = window?.parent?.document?.getElementById(
      resLookupFromHeader ? 'resLookupIframe' : 'signInOverlayIframe'
    );
    const iframeContent = iframe?.contentDocument?.scrollingElement?.scrollHeight;
    const paddingAround = 10 * 2;
    if (iframe && iframeContent) {
      iframe.style.height = `${iframeContent + paddingAround}px`;
    }
  };
  const sectionTitle = isDTT
    ? wasRemeberMeFlag
      ? props?.signIntoYourAccountTitle?.replace('{0}', name)
      : props?.subTitle
    : props?.signIntoYourAccountTitle;

  const signInPageId = `${SIGN_IN_PAGE_ID}-${modalId}`;

  const getSuccessBannerText = (): string => {
    if (signInEmailSuccess) return props.successBannerMessage;
    if (lookUpEmailSuccess) {
      const maskedEmail = maskEmail(lookUpMemberEmail?.trim() ?? '');
      return props.lookUpSuccessBannerMessage.replace('{0}', maskedEmail);
    }
    return '';
  };

  return (
    <StyledSignInContainerDiv
      id={signInPageId}
      data-component-name="m-account-SignIn"
      data-testid="account-SignIn"
      className={clsx(
        'px-md-0 res-lookup-overlay-book',
        isEAASignIn ? 'mx-auto mr-lg-auto grid-container-EAA' : !isReservation ? 'mr-lg-auto' : '',
        resLookupFromHeader && 'reslookup-container',
        isReservation ? 'd-flex flex-column justify-content-lg-end px-0' : !isFlyOut ? 'px-1' : '',
        isAccountModal && isReservation
          ? 'ml-0 ml-lg-auto mr-0'
          : isReservation
          ? 'ml-3 ml-md-0 ml-lg-auto mr-3 mr-md-0'
          : '',
        !isInsideAccountContainer &&
          (renderEmailSuccessSignInModal
            ? 'mt-2 mt-md-5 px-3'
            : isFlyOut && accountEvent?.qrvVariation === QrvType.EXPLORE
            ? 'mt-0'
            : 'mt-5 px-3')
      )}
      isReservation={isReservation}
      isFlyOut={isFlyOut}
    >
      {/* email success message for phase one */}
      {renderEmailSuccessSignInModal && (signInEmailSuccess || lookUpEmailSuccess) && (
        <div>
          <div className="email-sign-in-success-msg mb-5">
            <Messages messageType="success" className="px-2">
              <div className="t-font-s">{props?.successBannerHeader}</div>
              <RichText
                customClass="t-font-s"
                text={getSuccessBannerText()}
                componentId="signin-email-success-msg-title"
              />
              <Link
                text={props?.customerSupportCTALabel}
                linkClassName="m-link-tertiary-button-external"
                linkHref={props?.customerSupportCTAPath}
                target={'_blank'}
              />
            </Messages>
          </div>
        </div>
      )}
      {isFlyOut ? (
        accountEvent?.qrvVariation === QrvType.EXPLORE ? (
          <div className={clsx('mb-5')}>
            {/* Render QRV Explore labels here, replace with actual label props as needed */}
            <Heading
              variation={Types.headingType.subtitle}
              fontSize={Types.size.extraLarge}
              element={Types.tags.h3}
              titleText={
                wasRemeberMeFlag
                  ? `${props?.rememberedSignInHeader?.replace('{0}', name || '')}`
                  : props?.anonymousSignInHeader
              }
              disableCustomClickTrack={true}
            />
            {props?.signInSubHeader && (
              <Text
                copyText={props?.signInSubHeader}
                fontSize={Types.size.medium}
                element={Types.tags.span}
                customClass="mb-0"
              />
            )}
          </div>
        ) : (
          <div className={clsx('mb-5')}>
            {((wasRemeberMeFlag && !!props?.rememberedUserTextLabel) || !!props?.subTitle) && (
              <Heading
                variation={Types.headingType.subtitle}
                fontSize={Types.size.extraLarge}
                element={Types.tags.h3}
                titleText={
                  wasRemeberMeFlag ? `${props?.rememberedUserTextLabel?.replace('{0}', name || '')}` : props?.subTitle
                }
                disableCustomClickTrack={true}
              />
            )}
            {props?.sectionDescription && !accountEvent?.isGlobalHeaderSignIn && (
              <Text
                copyText={props?.sectionDescription}
                fontSize={Types.size.medium}
                element={Types.tags.span}
                customClass="mb-0"
              />
            )}
          </div>
        )
      ) : !sessionTimedOutPage ? (
        <div
          className={clsx(
            (isEAASignIn || isAccountModal) && errorsList?.length ? 'mb-3' : 'mb-4 pb-1',
            (resLookupFromHeader || isReservation) && !isCheckedIn && 'mt-4 mt-md-0 mt-lg-2'
          )}
        >
          {props?.oneClickJoinReservationTitle && (
            <Heading
              variation={Types.headingType.title}
              fontSize={Types.size.small}
              element={Types.tags.h3}
              titleText={props?.oneClickJoinReservationTitle}
            />
          )}

          {resLookupFromHeader || isReservation ? (
            isCheckedIn ? (
              <>
                <Heading
                  variation={Types.headingType.subtitle}
                  fontSize={isDesktopViewPort ? Types.size.extraLarge : Types.size.large}
                  element={Types.tags.h3}
                  titleText={props?.signIntoYourAccountTitle}
                  disableCustomClickTrack={true}
                  customClass="mt-5 mt-lg-0 mb-4"
                />
                <Heading
                  variation={Types.headingType.subtitle}
                  fontSize={Types.size.medium}
                  element={Types.tags.h3}
                  titleText={props?.subTitle}
                  disableCustomClickTrack={true}
                  customClass="mb-0"
                />
              </>
            ) : (
              <Heading
                variation={Types.headingType.subtitle}
                fontSize={Types.size.extraLarge}
                element={Types.tags.h3}
                titleText={props?.signIntoYourAccountTitle ? sectionTitle : props?.subTitle}
                disableCustomClickTrack={true}
              />
            )
          ) : renderEmailSuccessSignInModal ? (
            <>
              <Heading
                variation={Types.headingType.subtitle}
                fontSize={Types.size.extraLarge}
                titleText={
                  wasRemeberMeFlag ? `${props?.rememberedUserTextLabel?.replace('{0}', name || '')}` : props?.subTitle
                }
                element={Types.tags.div}
                customClass={'mb-2'}
              />
              {/* we need to show this label only when email success message is visible for phase one */}
              {signInEmailSuccess && (
                <Text
                  copyText={props?.sectionDescription} //new authorable field
                  fontSize={Types.size.medium}
                  element={Types.tags.div}
                  customClass="mb-1"
                />
              )}
            </>
          ) : (
            <Heading
              variation={isEAASignIn ? Types.headingType.title : Types.headingType.subtitle}
              fontSize={isEAASignIn ? Types.size.small : Types.size.medium}
              /** title is for your account title, subtitle for sign in */
              titleText={wasRemeberMeFlag || isEAASignIn ? props?.signIntoYourAccountTitle : props?.subTitle}
              element={isEAASignIn ? Types.tags.h2 : Types.tags.paragraph}
              customClass={clsx(wasRemeberMeFlag && !isEAASignIn ? 'mb-2' : 'mb-0')}
            />
          )}
          {wasRemeberMeFlag && !isEAASignIn && !isRedemptionDTT && !renderEmailSuccessSignInModal && (
            <Text
              copyText={`${props?.rememberedUserTextLabel?.replace('{0}', name || '')}`}
              fontSize={Types.size.small}
              element={Types.tags.paragraph}
              customClass="remember-me__text mb-0"
            />
          )}
        </div>
      ) : (
        // session timed out
        <div className="mb-4">
          <Heading
            variation={Types.headingType.subtitle}
            titleText={props?.sectionTitle}
            customClass="t-subtitle-m mb-2"
            element={Types.tags.paragraph}
          />
          <Text
            copyText={`${rememberedUser ? `${name}${isKOLocale ? '' : ','} ` : ''}${props?.sectionDescription}`}
            fontSize={Types.size.small}
            element={Types.tags.paragraph}
            customClass="t-font-s mb-0 remember-me__text"
          />
        </div>
      )}
      {/* error block */}
      {(isEAASignIn || isAccountModal || renderEmailSuccessSignInModal) && !!errorsList?.length ? (
        <div className="mb-4">
          <Messages messageType="error-sev1" className="account-page-error-msg px-2">
            {!!errorsList?.length && (
              <RichText customClass={''} text={errorsList[0] ?? ''} componentId="signin-error-msg-title" />
            )}
          </Messages>
        </div>
      ) : (
        isEAASignIn &&
        showEAASignInMsg && (
          <div className="eaa-sign-in-success-msg mb-4">
            <Messages messageType="success" className="px-2">
              <RichText
                customClass="t-font-s"
                text={props?.signInSuccessMsg}
                componentId="signin-eaa-success-msg-title"
              />
            </Messages>
          </div>
        )
      )}
      <form method="post" onSubmit={handleSubmit}>
        <div
          className={clsx(
            'form-field-contaioner',
            (formErrors.email !== undefined && formErrors.email !== '') || isFlyOut ? 'mb-4' : 'mb-5'
          )}
        >
          <InputTextField
            label={checkForCountry() ? props?.credentialsLabel : props?.emailOrMemberLabel}
            inputValue={formData.email}
            testId="email-text-field"
            showErrorMessage={formErrors.email !== undefined || isFcpError}
            messageToShow={shouldShowErrorMessage ? replaceText(formErrors?.email || '', [trackingPageName]) : ''}
            setErrorHtml={true}
            messageClass="error-label t-font-xs m-0 mt-1"
            inputMaxLength={80}
            infoLabel={isCheckedIn ? `${inputId}-email` : props?.emailOrMemberLabel?.toLowerCase() || ''}
            getInputProps={() => ({
              /** setReadOnly props for remeber case */
              disabled: disableButton,
              readOnly: wasRemeberMeFlag,
              autoComplete: 'off',
              onKeyUp: handleKeyPress,
              id: isCheckedIn ? `${inputId}-email-checkedin` : `${inputId}-email`,
            })}
            getLabelProps={() => ({
              htmlFor: isCheckedIn ? `${inputId}-email-checkedin` : `${inputId}-email`,
            })}
            className={clsx(
              'username-input',
              inlineLabel ? 'm-input-text-field username-input__inline' : 'm-input-field username-input__standard',
              formData.email !== undefined && formData.email !== '' && formErrors.email === undefined
                ? 'is-active'
                : '',
              wasRemeberMeFlag || disableButton ? 'disabled' : '',
              isAELocale && isResLookUpLoaded ? 'rtl-fix-req' : ''
            )} //is-error,is-active
            getInputValue={(val: string) => {
              if (!wasRemeberMeFlag) {
                setShowAccordionExampleErrorMsg(false);
                const hasError = !isChinaLocale
                  ? emailOrMemberNumberError(val)
                  : !(signMobileRegex.test(val) || signEmailRegex?.test(val));
                setFormErrors({
                  ...formErrors,
                  email: hasError ? props?.invalidEmailOrMemberError : undefined,
                });
                setFormData({ ...formData, email: val?.trim() });
              }
            }}
            customDownshiftLabel={true}
            inputTextFieldClassName="transfer-details-container__form__converted-points"
          />
          {!isEAASignIn && showAccordionExampleErrorMsg && checkForCountry() && !renderEmailSuccessSignInModal ? (
            <div
              className="accordion accordion-container m-accordion-container ps-0 mt-1"
              id="accordionExample-errormsg"
            >
              {/* TODO:// need confirmation on accordion atoms in mi-ui-lib */}
              <div className="accordion-item">
                <div className="p-0 accordion-header px-0 mb-1" id="headingOne-errormsg">
                  <button
                    className="accordion-button collapsed p-0 ms-0 border-0"
                    type="button"
                    id="accordian_button_errormsg"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOne-errormsg"
                    aria-expanded={toggleErrormsgAccoridon}
                    aria-controls="collapseOne-errormsg"
                    onClick={() => setToggleErrormsgAccordion(!toggleErrormsgAccoridon)}
                  >
                    <div className="m-accordion-title d-flex align-items-center justify-content-center">
                      <Text
                        copyText={props?.phoneNumberVerificationMessage}
                        fontSize={Types.size.extraSmall}
                        customClass={'t-font-xs mb-0 p-0 error-label'}
                        element={Types.tags.paragraph}
                      />
                      <Icon
                        iconClass={clsx(
                          `icon-arrow-down accordion-arrow-icon pl-2 icon-xs error-label ${
                            toggleErrormsgAccoridon ? 'icon-arrow-up' : 'icon-arrow-down'
                          }`
                        )}
                      ></Icon>
                    </div>
                  </button>
                </div>
                <div
                  id="collapseOne-errormsg"
                  className={clsx('accordion-collapse collapse', toggleErrormsgAccoridon ? 'show' : '')}
                  aria-labelledby="headingOne-errormsg"
                  data-bs-parent="#accordionExample-errormsg"
                >
                  <div
                    className="accordion-body t-font-xs error-label"
                    dangerouslySetInnerHTML={{ __html: props?.membershipVerificationMessage ?? '' }}
                  ></div>
                </div>
              </div>
              <RichText
                customClass={clsx(' t-font-xs error-label', toggleErrormsgAccoridon ? 'mt-3' : 'mt-1')}
                text={props?.membershipNumberWarningMessage ?? ''}
                componentId="signin-error-msg"
              />
            </div>
          ) : (
            ''
          )}
          {isEAASignIn && isFcpError && !renderEmailSuccessSignInModal && (
            <>
              <Accordion
                id="eaa-fcp-error"
                headerChildren={
                  <Text
                    element={Types.tags.span}
                    fontSize={Types.size.medium}
                    copyText={props?.eaaFcpInlineErrorHeaderMessage}
                    customClass="t-font-xs error-label"
                  />
                }
                customClass="eaa-fcp-error error-label"
              >
                <Text
                  element={Types.tags.span}
                  fontSize={Types.size.medium}
                  copyText={props?.eaaFcpInlineErrorBodyMessage}
                  customClass="t-font-xs error-label"
                />
              </Accordion>
              <Text
                element={Types.tags.span}
                fontSize={Types.size.medium}
                copyText={props?.eaaFcpSecondInlineErrorMessage}
                customClass="t-font-xs error-label"
              />
            </>
          )}
        </div>
        <div
          className={clsx(
            'form-field-contaioner',
            isFlyOut ? 'mb-4' : 'mb-md-5 mb-4',
            formErrors.pwrd && inlineLabel && 'password-error'
          )}
        >
          <InputTextField
            type={showPassword ? 'text' : 'password'}
            label={props?.pwrdLabel}
            testId="sign-in-pwrd"
            inputValue={formData.pwrd}
            inputMaxLength={20}
            setErrorHtml={true}
            getInputProps={() => ({
              disabled: disableButton,
              autoComplete: 'off',
              onKeyUp: handleKeyPress,
              id: `${inputId}-password`,
            })}
            // iconAriaLabel={`toggle ${props?.passwordLabel}`}
            className={clsx(
              inlineLabel ? 'm-input-text-field' : 'm-input-field',
              formData.pwrd !== undefined && formData.pwrd !== '' && formErrors.pwrd === undefined ? 'is-active' : '',
              disableButton && 'disabled',
              isAELocale && isResLookUpLoaded ? 'rtl-fix-req' : ''
            )} //is-error
            showIcon={true}
            messageClass="error-label t-font-xs m-0 mt-1"
            iconClass={clsx('password-icon', showPassword ? 'icon-visibility' : 'icon-visibility-off')}
            showErrorMessage={formErrors.pwrd !== undefined}
            messageToShow={formErrors?.pwrd}
            iconOnClick={toggleShowPassword}
            infoLabel={(props?.signInLabel + ' ' + props?.pwrdLabel)?.toLowerCase() || ''}
            trailingIconProps={{
              role: 'button',
              'aria-pressed': showPassword,
              'aria-label': `toggle ${props?.pwrdLabel}`,
              onKeyDown: (e: KeyboardEvent) => {
                if (e.key === 'Enter' || e.code === '13') {
                  toggleShowPassword();
                }
              },
            }}
            getLabelProps={() => ({
              htmlFor: `${inputId}-password`,
            })}
            getInputValue={(val: string) => {
              setFormData({ ...formData, pwrd: val?.trim() });
            }}
            customDownshiftLabel={true}
          />
        </div>

        {!isEAASignIn && (
          <div className={clsx('form-field-contaioner', renderEmailSuccessSignInModal ? 'mb-5' : 'mb-md-5 mb-4')}>
            <CheckBox
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                // handle rember me from session or other value
                setRememberMe(e.target.checked);
                sessionStorage.setItem(REMEMBER_ME_KEY, `${e.target.checked}`);
              }}
              checked={rememberMe}
              data-testid="remember-me"
              checkboxName="remember_me"
              checkboxLabel={props?.rememberMeLabel}
              checkboxId={checkBoxId}
              disabled={disableButton}
              inputClassName={disableButton ? 'disabled' : ''}
              custom_click_track_value={`${trackingPageName}|${REMEMBER_ME}|${INTERNAL}|`}
            />
          </div>
        )}
        <div className={clsx('form-field-contaioner', isFlyOut ? 'mb-5' : 'mb-4')}>
          <Button
            type={Types.ButtonTypeVariation.Submit}
            testId="sign-in-btn-submit"
            isDisabled={disableButton || isLoading}
            className={clsx(
              'm-button-m width-mobile-100 d-flex-sm justify-content-center align-items-center',
              disableButton ? 'disabled' : '',
              isFlyOut ? 'px-4' : 'width-mobile-100__flyout'
            )}
            {...(!isEAASignIn && {
              custom_click_track_value: `${trackingPageName}|${SIGNIN}|${INTERNAL}|`,
            })}
            ariaLabel={props.signInLabel}
          >
            <div className={clsx(!isLoading ? 'd-none' : 'm-spinner-s')} data-testid="loading-spinner"></div>
            <div className={clsx(isLoading ? 'd-none' : '')}>{props?.signInLabel}</div>
          </Button>
        </div>
      </form>
      <div
        className={clsx(
          'link-container',
          isReservation ? `${!isDTT && (wasRemeberMeFlag ? 'd-flex' : 'd-flex d-md-block')} mb-5 mb-lg-0` : ''
        )}
      >
        {(!renderEmailSuccessSignInModal || lookUpEmailSuccess) && (
          <div className="link-container__item pb-1">
            <Link
              text={props.forgotPwrdLabel}
              linkClassName="m-link-action"
              linkHref={forgotPasswordLink}
              target={isOverlay ? '_parent' : '_self'}
              linkType="internal"
              custom_click_track_value={`${trackingPageName}|${FORGOT_PASSWORD}|${INTERNAL}|`}
              {...(enablePhaseOne
                ? {
                    callback: event => {
                      event.preventDefault();
                      handleClick(forgotPasswordLink);
                    },
                  }
                : {})}
            />
          </div>
        )}
        {!isEAASignIn && !props?.oneClickJoinReservationTitle && (
          <div
            className={clsx(
              'link-container__item',
              isReservation && !isDTT ? (wasRemeberMeFlag ? 'ml-5' : 'ml-md-0 ml-5') : ''
            )}
          >
            {wasRemeberMeFlag ? (
              <Button
                buttonCopy={props?.clearRememberedAccountLabel}
                className="m-link-action clear-remember-me p-0"
                linkType="internal"
                id="remember_me"
                callback={handleClearRememberUserClick}
                custom_click_track_value={`${trackingPageName}|${CLEAR_REMEMBER_ME}|${INTERNAL}|`}
              />
            ) : (
              ((!isCheckedIn && !renderEmailSuccessSignInModal) || lookUpEmailSuccess) && (
                <Link
                  text={props?.activateOnlineAccountLabel}
                  linkClassName="m-link-action"
                  target={isOverlay ? '_parent' : '_self'}
                  linkType="internal"
                  id="remember_me"
                  linkHref={props.activateOnlineAccountCtaPath}
                  custom_click_track_value={`${trackingPageName}|${ACTIVATE_ONLINE_ACCOUNT}|${INTERNAL}|`}
                  {...(enablePhaseOne
                    ? {
                        callback: event => {
                          event.preventDefault();
                          handleClick(props?.activateOnlineAccountCtaPath);
                        },
                      }
                    : {})}
                />
              )
            )}
          </div>
        )}
      </div>
      {isUSLocale && !isEAASignIn && !isReservation && props.accordionDescription && !isFlyOut && (
        <div className="accordion accordion-container m-accordion-container pt-3 ps-0" id="accordionExample-signin">
          {/* TODO:// need confirmation on accordion atoms in mi-ui-lib */}
          <div className="accordion-item">
            <div className="p-0 accordion-header px-0 mb-2" id="headingOne-signin">
              <button
                className="accordion-button collapsed p-0 ms-0 border-0"
                type="button"
                id="accordian_button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseOne-signin"
                aria-expanded={toggleAccoridon}
                aria-controls="collapseOne-signin"
                onClick={() => setToggleAccordion(!toggleAccoridon)}
              >
                <div className="m-accordion-title d-flex align-items-center">
                  <p className="t-label-alt-s mb-0"> {props.accordionLabel}</p>
                  <span
                    className={clsx(
                      'icon-arrow-down accordion-arrow-icon pl-2 icon-xs',
                      toggleAccoridon ? 'icon-arrow-up' : 'icon-arrow-down'
                    )}
                  ></span>
                </div>
              </button>
            </div>
            <div
              id="collapseOne-signin"
              className={clsx('accordion-collapse collapse', toggleAccoridon ? 'show' : '')}
              aria-labelledby="headingOne-signin"
              data-bs-parent="#accordionExample-signin"
            >
              <div
                className="accordion-body t-font-s"
                dangerouslySetInnerHTML={{ __html: props?.accordionDescription ?? '' }}
              ></div>
            </div>
          </div>
        </div>
      )}
      {isEAASignIn && !renderEmailSuccessSignInModal && (
        <Text
          customClass="t-font-xs pt-2 mt-5"
          copyText={props?.copyrightStatement?.replace('{0}', String(moment().year()))}
          fontSize={Types.size.extraSmall}
          element={Types.tags.paragraph}
        />
      )}
      {renderEmailSuccessSignInModal && <SignInEmailSuccessModalStyle />}
    </StyledSignInContainerDiv>
  );
});
export const SignInConfig = {
  emptyLabel: 'signin',
  isEmpty: false,
  resourceType: `mi-aem-account/components/content/signin`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SignInEditable = (props: any) => {
  return props.cqPath?.includes('datapagecomponent') ? (
    <SignIn {...props} />
  ) : (
    <EditableComponent config={SignInConfig} {...props}>
      <SignIn {...props} />
    </EditableComponent>
  );
};
